import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Topbar from '../../components/shared/Topbar';
import EmployeeSidebar from '../../components/employee/EmployeeSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import '../../styles/global.css';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmt = (n) => parseFloat(n || 0).toFixed(2);

function leaseStatus(leaseEnd) {
  if (!leaseEnd) return { label: 'No Date', badge: 'gray', key: 'no_date' };
  const end = new Date(leaseEnd);
  const now = new Date();
  const diffDays = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { label: 'Expired', badge: 'red', key: 'expired', days: diffDays };
  if (diffDays <= 60) return { label: `${diffDays}d left`, badge: 'orange', key: 'expiring_soon', days: diffDays };
  return { label: 'Active', badge: 'green', key: 'active', days: diffDays };
}

const LEASE_LABELS = {
  free_market: 'Free Market', rent_stabilized: 'Rent Stabilized', rent_controlled: 'Rent Controlled',
  section_8: 'Section 8', scrie: 'SCRIE', drie: 'DRIE', employee: 'Employee', sublease: 'Sublease', other: 'Other',
};
const RESTRICTED = ['section_8', 'scrie', 'drie', 'rent_controlled'];

export default function EmployeeTenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterLease, setFilterLease] = useState('all');
  const [filterBilling, setFilterBilling] = useState('all');
  const [filterLeaseStatus, setFilterLeaseStatus] = useState('all');
  const [filterOptOut, setFilterOptOut] = useState('all');
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    api.get(`/employee/tenants?status=${filterStatus}`)
      .then(res => setTenants(res.data.tenants))
      .catch(() => toast.error('Could not load tenants'))
      .finally(() => setLoading(false));
  }, [filterStatus]);

  useEffect(() => {
    api.get('/employee/tenants?status=pending_verification')
      .then(res => setPendingCount(res.data.tenants?.length || 0))
      .catch(() => {});
  }, []);

  const filtered = tenants.filter(t => {
    if (search && !`${t.first_name} ${t.last_name} ${t.email} ${t.unit_number} ${t.property_name}`.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterLease !== 'all' && (t.lease_category || 'free_market') !== filterLease) return false;
    if (filterBilling !== 'all') {
      const et = t.enrollment_type || 'auto';
      if (filterBilling !== et) return false;
    }
    if (filterLeaseStatus !== 'all') {
      const ls = leaseStatus(t.lease_end);
      if (filterLeaseStatus !== ls.key) return false;
    }
    if (filterOptOut === 'opted_out' && !t.email_opt_out) return false;
    if (filterOptOut === 'subscribed' && t.email_opt_out) return false;
    return true;
  });

  const optInTenants = tenants.filter(t => t.enrollment_type === 'opt_in_required');


  const exportCSV = () => {
    const headers = ['First Name', 'Last Name', 'Email', 'Phone', 'Unit', 'Property', 'Lease Type', 'Lease Category', 'Enrollment Type', 'Billing Eligible', 'Lease Start', 'Lease End', 'Subscription Status', 'Plan', 'Monthly Rate', 'Status', 'Enrolled'];
    const rows = filtered.map(t => [
      t.first_name, t.last_name, t.email, t.phone || '',
      t.unit_number || '', t.property_name || '',
      t.lease_type || '', t.lease_category || '', t.enrollment_type || 'auto',
      t.billing_eligible === false ? 'No' : 'Yes',
      t.lease_start ? new Date(t.lease_start).toLocaleDateString() : '',
      t.lease_end ? new Date(t.lease_end).toLocaleDateString() : '',
      t.subscription_status || '', t.plan_name || '',
      t.bulknet_price ? parseFloat(t.bulknet_price).toFixed(2) : '',
      t.status, t.created_at ? new Date(t.created_at).toLocaleDateString() : ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tenants_export_' + new Date().toISOString().split('T')[0] + '.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="app-layout">
      <Topbar role="employee" />
      <div className="dashboard-layout">
        <EmployeeSidebar />
        <main id="main-content" className="main-content">
          <div className="page-title">All Tenants</div>
          <div className="page-sub">Platform-wide view of all tenant accounts.</div>

          <div className="metric-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="metric-card">
              <div className="metric-label">Total shown</div>
              <div className="metric-value">{filtered.length}</div>
            </div>
            <div className="metric-card">
              <div className="metric-label">Auto-Enrolled</div>
              <div className="metric-value green">{tenants.filter(t => (t.enrollment_type || 'auto') === 'auto').length}</div>
            </div>
            <div className="metric-card" style={{ borderColor: optInTenants.length > 0 ? '#fde68a' : undefined }}>
              <div className="metric-label">Opt-In Required</div>
              <div className="metric-value" style={{ color: optInTenants.length > 0 ? 'var(--orange)' : undefined }}>
                {optInTenants.length}
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-label">No payment method</div>
              <div className="metric-value" style={{ color: 'var(--orange)' }}>
                {filtered.filter(t => !t.stripe_customer_id).length}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
            <input className="form-input" style={{ maxWidth: 300 }}
              placeholder="Search by name, email, unit, property..."
              value={search} onChange={e => setSearch(e.target.value)} />
            <select className="form-input" style={{ maxWidth: 200 }} aria-label="Filter by status"
              value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setLoading(true); }}>
              <option value="active">Active</option>
              <option value="pending_verification">Pending Verification{pendingCount > 0 ? ` (${pendingCount})` : ''}</option>
              <option value="inactive">Inactive</option>
              <option value="rejected">Rejected</option>
            </select>
            <select className="form-input" style={{ maxWidth: 180 }} aria-label="Filter by lease category"
              value={filterLease} onChange={e => setFilterLease(e.target.value)}>
              <option value="all">All Lease Types</option>
              {Object.entries(LEASE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select className="form-input" style={{ maxWidth: 180 }} aria-label="Filter by enrollment type"
              value={filterBilling} onChange={e => setFilterBilling(e.target.value)}>
              <option value="all">All Enrollment</option>
              <option value="auto">Auto-Enrolled</option>
              <option value="opt_in_required">Opt-In Required</option>
            </select>
            <select className="form-input" style={{ maxWidth: 180 }} aria-label="Filter by lease status"
              value={filterLeaseStatus} onChange={e => setFilterLeaseStatus(e.target.value)}>
              <option value="all">All Lease Status</option>
              <option value="active">Active Lease</option>
              <option value="expiring_soon">Expiring Soon (60d)</option>
              <option value="expired">Expired</option>
              <option value="no_date">No Date</option>
            </select>
            <select className="form-input" style={{ maxWidth: 160 }} aria-label="Filter by email opt-out"
              value={filterOptOut} onChange={e => setFilterOptOut(e.target.value)}>
              <option value="all">All Email</option>
              <option value="subscribed">Subscribed</option>
              <option value="opted_out">Opted Out</option>
            </select>
          </div>

          <div className="card">
            <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div className="card-title">Tenants ({filtered.length})</div>
              <button className="btn btn-outline btn-sm" onClick={exportCSV}>Export CSV</button>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Property</th>
                    <th>Unit</th>
                    <th>Plan</th>
                    <th>Monthly</th>
                    <th>Payment</th>
                    <th>Lease</th>
                    <th>Enrollment</th>
                    <th>Lease Status</th>
                    <th>Status</th>
                    <th>Enrolled</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 && (
                    <tr><td colSpan={11} style={{ textAlign: 'center', color: 'var(--text-sub)' }}>No tenants found</td></tr>
                  )}
                  {filtered.map(t => (
                    <tr key={t.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{t.first_name} {t.last_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-sub)' }}>
                          {t.email}
                          {t.email_opt_out && <span className="badge badge-red" style={{ marginLeft: 6 }}>Opted Out</span>}
                        </div>
                        {t.phone && <div style={{ fontSize: 11, color: 'var(--text-sub)' }}>{t.phone}</div>}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        <div>{t.property_name}</div>
                        <div style={{ color: 'var(--text-sub)', fontSize: 11 }}>{t.landlord_name}</div>
                      </td>
                      <td style={{ fontWeight: 700 }}>{t.unit_number || '—'}</td>
                      <td style={{ fontSize: 12 }}>{t.plan_name || '—'}</td>
                      <td style={{ fontWeight: 700 }}>{t.bulknet_price ? `$${fmt(t.bulknet_price)}` : '—'}</td>
                      <td>
                        {t.stripe_customer_id ? (
                          <span className="badge badge-green">On file</span>
                        ) : (
                          <span className="badge badge-orange">Missing</span>
                        )}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {(() => {
                          const cat = t.lease_category || 'free_market';
                          const isRestricted = RESTRICTED.includes(cat);
                          return (
                            <span className={`badge ${isRestricted ? 'badge-red' : 'badge-gray'}`}>
                              {LEASE_LABELS[cat] || cat}
                            </span>
                          );
                        })()}
                        {t.voucher_type && <div style={{ fontSize: 10, color: 'var(--text-sub)', marginTop: 2 }}>{t.voucher_type}</div>}
                      </td>
                      <td>
                        {(t.enrollment_type || 'auto') === 'opt_in_required'
                          ? <span className="badge badge-orange">Opt-In Required</span>
                          : <span className="badge badge-green">Auto-Enrolled</span>
                        }
                      </td>
                      <td>
                        {(() => {
                          const ls = leaseStatus(t.lease_end);
                          return (
                            <>
                              <span className={`badge badge-${ls.badge}`}>{ls.label}</span>
                              {t.lease_end && <div style={{ fontSize: 10, color: 'var(--text-sub)', marginTop: 2 }}>{fmtDate(t.lease_end)}</div>}
                            </>
                          );
                        })()}
                      </td>
                      <td>
                        <span className={`badge badge-${t.status === 'active' ? 'green' : t.status === 'pending_verification' ? 'orange' : t.status === 'rejected' ? 'red' : 'gray'}`}>
                          {t.status === 'pending_verification' ? 'Pending' : t.status}
                        </span>
                        {t.status === 'pending_verification' && (
                          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                            <button className="btn btn-sm" style={{ padding: '2px 8px', fontSize: 11, background: '#16a34a', color: 'white', border: 'none', borderRadius: 4 }}
                              onClick={async () => {
                                try {
                                  await api.post('/auth/tenant/verify-action', { tenant_id: t.id, action: 'approve' });
                                  toast.success('Tenant approved');
                                  setLoading(true);
                                  api.get(`/employee/tenants?status=${filterStatus}`).then(res => setTenants(res.data.tenants)).finally(() => setLoading(false));
                                } catch { toast.error('Could not approve'); }
                              }}>Approve</button>
                            <button className="btn btn-sm" style={{ padding: '2px 8px', fontSize: 11, background: '#dc2626', color: 'white', border: 'none', borderRadius: 4 }}
                              onClick={async () => {
                                try {
                                  await api.post('/auth/tenant/verify-action', { tenant_id: t.id, action: 'reject' });
                                  toast.success('Tenant rejected');
                                  setLoading(true);
                                  api.get(`/employee/tenants?status=${filterStatus}`).then(res => setTenants(res.data.tenants)).finally(() => setLoading(false));
                                } catch { toast.error('Could not reject'); }
                              }}>Reject</button>
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize: 12 }}>{fmtDate(t.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
